/**
 * On-demand Leaflet loader.
 *
 * Leaflet's CSS and JS used to sit in public/index.html as ordinary
 * render-blocking tags, so every page on every site paid for them: two
 * requests to a third-party origin, plus the DNS and TLS handshake to reach
 * it, before anything could paint. Exactly two components in the app draw a
 * map — SalesMap and PublicListingDetail — so the home page, the valuation
 * form and every CRM screen were blocking on a library they never call.
 *
 * The tags are the same ones, with the same pinned version and the same SRI
 * hashes; they are just injected the moment a map actually needs them.
 *
 * Concurrent callers share one promise, so two maps on a page load it once,
 * and a repeat call after it has loaded resolves immediately.
 */

const LEAFLET_VERSION = '1.9.4';
const CSS_URL = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const JS_URL = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
const CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';

let pending = null;

function injectOnce(selector, build) {
    if (document.querySelector(selector)) return null;
    const el = build();
    document.head.appendChild(el);
    return el;
}

/**
 * Resolves with the Leaflet global (`window.L`), loading it first if needed.
 * Rejects if the CDN cannot be reached, so a caller can leave its container
 * empty rather than throwing inside a render.
 */
export function loadLeaflet() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Leaflet needs a browser'));
    }
    if (window.L) return Promise.resolve(window.L);
    if (pending) return pending;

    pending = new Promise((resolve, reject) => {
        injectOnce(`link[href="${CSS_URL}"]`, () => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = CSS_URL;
            link.integrity = CSS_SRI;
            link.crossOrigin = '';
            return link;
        });

        const existing = document.querySelector(`script[src="${JS_URL}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(window.L));
            existing.addEventListener('error', () => reject(new Error('Leaflet failed to load')));
            return;
        }

        const script = document.createElement('script');
        script.src = JS_URL;
        script.integrity = JS_SRI;
        script.crossOrigin = '';
        script.async = true;
        script.onload = () => {
            if (window.L) resolve(window.L);
            else reject(new Error('Leaflet loaded but window.L is missing'));
        };
        script.onerror = () => {
            // Let a later attempt retry rather than caching the failure for
            // the life of the page — a map is worth a second chance on a
            // flaky connection.
            pending = null;
            reject(new Error('Leaflet failed to load'));
        };
        document.head.appendChild(script);
    });

    return pending;
}

export default loadLeaflet;
